import { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  /** Show delay in ms; 0 = instant */
  showDelay?: number;
  /** Hide delay in ms; 0 = instant */
  hideDelay?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export default function Tooltip({
  content,
  children,
  showDelay = 0,
  hideDelay = 0,
  position = 'top',
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleEnter = () => {
    clearTimers();
    if (showDelay > 0) {
      showTimerRef.current = setTimeout(() => setVisible(true), showDelay);
    } else {
      setVisible(true);
    }
  };

  const handleLeave = () => {
    clearTimers();
    if (hideDelay > 0) {
      hideTimerRef.current = setTimeout(() => setVisible(false), hideDelay);
    } else {
      setVisible(false);
    }
  };

  useEffect(() => {
    return clearTimers;
  }, []);

  return (
    <span className="tooltip-wrap" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      {children}
      {visible && (
        <span className={`tooltip-bubble tooltip-bubble--${position}`} role="tooltip">
          {content}
        </span>
      )}
    </span>
  );
}
